import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useEffectiveTenantId } from '@/hooks/useEffectiveTenantId';
import { useTranslation } from 'react-i18next';

export type AIMode = 'auto' | 'suggestions' | 'manual';

interface UseAIAgentModeProps {
  conversationId: string;
}

interface UseAIAgentModeExtendedProps extends UseAIAgentModeProps {
  generateAISuggestion?: (messageContent: string) => Promise<void>;
  onModeChange?: (conversationId: string, newMode: AIMode) => void;
}

// Cache for tenant schemas to avoid repeated DB calls
const tenantSchemaCache = new Map<string, string>();

export function useAIAgentMode({ conversationId, generateAISuggestion, onModeChange }: UseAIAgentModeExtendedProps) {
  const { user } = useAuth();
  const effectiveTenantId = useEffectiveTenantId();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  console.log('🎯 useAIAgentMode INIT:', { 
    conversationId, 
    effectiveTenantId, 
    hasGenerateAISuggestion: !!generateAISuggestion 
  });
  const [aiMode, setAIMode] = useState<AIMode>('auto');
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const modeChangeTimeoutRef = useRef<NodeJS.Timeout>();

  // Get cached tenant schema or fetch it
  const getTenantSchema = useCallback(async (): Promise<string | null> => {
    if (!effectiveTenantId) return null;
    
    // Check cache first
    if (tenantSchemaCache.has(effectiveTenantId)) {
      return tenantSchemaCache.get(effectiveTenantId)!;
    }

    // Fetch from database
    const { data: tenantData } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', effectiveTenantId)
      .single();

    if (tenantData?.schema_name) {
      tenantSchemaCache.set(effectiveTenantId, tenantData.schema_name);
      return tenantData.schema_name;
    }

    return null;
  }, [effectiveTenantId]);

  // Load current mode from conversation
  const loadMode = useCallback(async () => {
    if (!user || !conversationId || !effectiveTenantId) {
      return;
    }

    try {
      setLoading(true);
      
      const tenantSchema = await getTenantSchema();
      if (!tenantSchema) {
        console.error('🎯 useAIAgentMode: No tenant schema found');
        setAIMode('auto');
        return;
      }

      // Get conversation's AI mode directly from database function
      const { data: conversationMode, error } = await supabase.rpc('get_conversation_ai_mode', {
        p_tenant_schema: tenantSchema,
        p_conversation_id: conversationId
      });

      if (error) {
        console.error('🎯 useAIAgentMode: Error loading conversation AI mode:', error);
        setAIMode('auto');
        return;
      }

      // Set the conversation's AI mode
      const currentMode = conversationMode || 'auto';
      setAIMode(currentMode as AIMode);
    } catch (error) {
      console.error('🎯 useAIAgentMode: Error in loadMode:', error);
      setAIMode('auto');
    } finally {
      setLoading(false);
    }
  }, [user, conversationId, effectiveTenantId, getTenantSchema]);


  // Update mode for conversation with optimistic updates and caching
  const changeMode = useCallback(async (newMode: AIMode) => {
    console.log('🎯 useAIAgentMode changeMode CALLED:', { newMode, conversationId, effectiveTenantId });
    if (!user || !conversationId || !effectiveTenantId) {
      console.log('🎯 useAIAgentMode changeMode BLOCKED - missing required data:', { 
        hasUser: !!user, 
        hasConversationId: !!conversationId, 
        hasEffectiveTenantId: !!effectiveTenantId 
      });
      return;
    }

    // Clear any pending mode change timeout
    if (modeChangeTimeoutRef.current) {
      clearTimeout(modeChangeTimeoutRef.current);
    }

    // Optimistic update with immediate UI feedback
    const previousMode = aiMode;
    setAIMode(newMode);

    try {
      // Use cached tenant schema for faster response
      const tenantSchema = await getTenantSchema();
      if (!tenantSchema) {
        console.error('🎯 useAIAgentMode: No tenant schema found');
        setAIMode(previousMode);
        toast({
          title: t('configurationErrorTitle'),
          description: t('tenantNotFound'),
          variant: "destructive",
        });
        return;
      }

      // Update the conversation's AI mode using database function
      // Also assign current user to conversation (Last Touch Wins pattern)
      const { error: updateError } = await supabase.rpc('set_conversation_ai_mode', {
        tenant_schema: tenantSchema,
        conversation_id_param: conversationId,
        new_mode: newMode,
        auth_user_id: user?.id || null  // Pass auth.users.id for user assignment
      });

      if (updateError) {
        console.error('🎯 useAIAgentMode: Update failed:', updateError);
        setAIMode(previousMode);
        toast({
          title: t('databaseErrorTitle'),
          description: t('updateAiModeFailed', { message: updateError.message }),
          variant: "destructive",
        });
        return;
      }

      // Process AI actions based on mode - use timeout to prevent UI flickering
      if (newMode === 'suggestions') {
        setIsProcessing(true);
        
        // Set a timeout to ensure mode stays in 'suggestions' for UI display
        modeChangeTimeoutRef.current = setTimeout(async () => {
          try {
            await processUnansweredMessagesForSuggestions(effectiveTenantId, tenantSchema, conversationId, generateAISuggestion);
          } finally {
            setIsProcessing(false);
          }
        }, 100); // Small delay for UI consistency
      } else if (newMode === 'auto') {
        setIsProcessing(true);
        
        modeChangeTimeoutRef.current = setTimeout(async () => {
          try {
            await processUnansweredMessages(effectiveTenantId, tenantSchema, conversationId, newMode);
          } finally {
            setIsProcessing(false);
          }
        }, 100);
      }

      const modeLabels = {
        auto: t('fullAutoAI'),
        suggestions: t('aiSuggestions'),
        manual: t('manualOnly')
      };

      const modeDescriptions = {
        auto: t('fullAutoAIDescription'),
        suggestions: t('aiSuggestionsDescription'),
        manual: t('manualOnlyDescription')
      };

      toast({
        title: t('aiModeEnabled', { mode: modeLabels[newMode] }),
        description: modeDescriptions[newMode],
        duration: 2000, // Shorter duration for faster feedback
      });

      // Notify parent component of mode change
      onModeChange?.(conversationId, newMode);
    } catch (error) {
      console.error('Error in changeMode:', error);
      setAIMode(previousMode);
      toast({
        title: t('error'),
        description: t('aiModeUpdateFailed'),
        variant: "destructive",
      });
    }
  }, [user, conversationId, toast, aiMode, effectiveTenantId, getTenantSchema, t]);

  // Define the function for processing unanswered messages
  const processUnansweredMessages = async (
    tenantId: string, 
    tenantSchema: string, 
    conversationId: string,
    mode: AIMode = 'auto'
  ) => {
    try {
      console.log('🎯 useAIAgentMode: Checking for unanswered customer messages...');
      
      // Prevent duplicate processing by checking for recent AI requests
      const requestKey = `ai-process-${conversationId}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
      if ((window as any).pendingAIRequests?.[requestKey]) {
        console.log('⏸️ AI request already pending for this conversation, preventing duplicate processing');
        return;
      }
      
      // Get recent messages from this conversation
      const { data: messages, error: messagesError } = await supabase.rpc('get_tenant_messages_direct', {
        tenant_schema: tenantSchema,
        conversation_id_param: conversationId,
        limit_count: 10 // Get last 10 messages to check for unanswered ones
      });

      if (messagesError) {
        console.error('🎯 useAIAgentMode: Error fetching messages for unanswered check:', messagesError);
        return;
      }

      if (!messages || messages.length === 0) {
        console.log('🎯 useAIAgentMode: No messages found in conversation');
        return;
      }

      // Sort messages by creation time (newest first)
      const sortedMessages = messages.sort((a: Record<string, unknown>, b: Record<string, unknown>) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      // Find the last customer message that's not a reaction or empty
      const lastCustomerMessage = sortedMessages.find((msg: Record<string, unknown>) => 
        msg.sender_type === 'customer' && 
        msg.message_type !== 'reaction' && 
        msg.content?.trim() !== '' &&
        msg.content !== 'EMPTY'
      );
      
      if (!lastCustomerMessage) {
        console.log('🎯 useAIAgentMode: No substantive customer messages found');
        return;
      }

      // Check if there's any AI or agent response after the last customer message
      const customerMessageTime = new Date(lastCustomerMessage.created_at).getTime();
      const hasSubsequentResponse = sortedMessages.some((msg: Record<string, unknown>) => {
        const msgTime = new Date(msg.created_at).getTime();
        const isResponse = msgTime > customerMessageTime && 
               (msg.sender_type === 'user' || msg.sender_type === 'agent' || msg.sender_type.startsWith('ai'));
        
        if (isResponse) {
          console.log(`🎯 useAIAgentMode: Found response after customer message - sender_type: ${msg.sender_type}`);
        }
        
        return isResponse;
      });

      if (hasSubsequentResponse) {
        console.log('🎯 useAIAgentMode: Customer message already has a response');
        return;
      }

      // Get customer details for the AI call
      const { data: customerData } = await supabase.rpc('get_tenant_conversations_with_details', {
        p_tenant_schema: tenantSchema,
        p_limit: 100
      });

      const conversation = customerData?.find((c: Record<string, unknown>) => c.id === conversationId);
      if (!conversation) {
        console.log('🎯 useAIAgentMode: Could not find conversation details');
        return;
      }

      // Validate that conversation has an assigned AI agent
      if (!conversation.ai_agent_id) {
        console.error('❌ useAIAgentMode: Conversation has no assigned AI agent');
        toast({
          title: t('configurationErrorTitle'),
          description: t('noAgentAssigned'),
          variant: "destructive",
        });
        return;
      }

      console.log('🎯 useAIAgentMode: Found unanswered customer message, triggering AI response...', {
        conversationId,
        customerMessage: lastCustomerMessage.content?.substring(0, 50),
        timestamp: new Date().toISOString()
      });
      
      // Show user feedback
      toast({
        title: t('aiProcessingPreviousTitle'),
        description: t('aiProcessingPreviousDescription'),
        duration: 4000,
      });

      // Mark request as pending to prevent duplicates
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
      if (!(window as any).pendingAIRequests) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
        (window as any).pendingAIRequests = {};
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
      if ((window as any).pendingAIRequests[requestKey]) {
        console.log('🎯 useAIAgentMode: AI request already in progress for this conversation');
        return;
      }

      // Mark request as pending
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
      if (!(window as any).pendingAIRequests) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
        (window as any).pendingAIRequests = {};
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
      (window as any).pendingAIRequests[requestKey] = true;

      try {
        // Use agent ID from conversation (assigned by webhook during conversation creation)
        // Workflow-executor will auto-detect the workflow from the agent's active workflows
        const agentId = conversation.ai_agent_id;

        console.log('🎯 useAIAgentMode: Using conversation agent for workflow execution', {
          conversationId,
          agentId,
          agentSource: 'conversation.ai_agent_id',
          customerMessage: lastCustomerMessage.content?.substring(0, 50)
        });

        // Use workflow-executor first (it will handle workflow detection internally)
        // This ensures proper workflow execution with WhatsApp sending
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 45000); // 45 second timeout (unified)

        console.log('🎯 useAIAgentMode: Invoking workflow-executor...', {
          conversationId,
          customerMessage: lastCustomerMessage.content?.substring(0, 50),
          mode,
          agentId: agentId,
          timestamp: new Date().toISOString()
        });

        const { data: aiResponse, error: aiError } = await supabase.functions.invoke('workflow-executor', {
          body: {
            context: {
              tenantId: tenantId,
              tenantSchema: tenantSchema,
              conversationId: conversationId,
              customerPhone: conversation.customer_phone,
              customerMessage: lastCustomerMessage.content,
              customerName: conversation.customer_name,
              messageType: 'text',
              agentId: agentId
              // workflowId removed - workflow-executor auto-detects from agent
            }
          }
        });
        
        clearTimeout(timeoutId);
        
        console.log('🎯 useAIAgentMode: AI agent response received:', {
          success: aiResponse?.success,
          messageId: aiResponse?.messageId,
          error: aiError?.message || 'None',
          timestamp: new Date().toISOString()
        });
        
        console.log('🎯 useAIAgentMode: Workflow-executor response received:', {
          hasError: !!aiError,
          responseKeys: aiResponse ? Object.keys(aiResponse) : [],
          success: aiResponse?.success,
          messageId: aiResponse?.messageId,
          error: aiError?.message || 'None'
        });

        if (aiError) {
          console.error('🎯 useAIAgentMode: AI agent error:', aiError);
          throw aiError;
        }

        // Be more lenient with success validation - sometimes the message is sent even without explicit success flag
        if (aiResponse?.success === false || (aiResponse != null && 'success' in aiResponse && !aiResponse.success)) {
          console.error('🎯 useAIAgentMode: AI agent explicitly failed:', aiResponse);
          throw new Error(`AI agent failed: ${aiResponse?.error || 'Unknown error'}`);
        }

        // If we have a response but no explicit messageId, log it but don't fail if we have other success indicators
        if (!aiResponse?.messageId && !aiResponse?.message_id) {
          console.warn('🎯 useAIAgentMode: AI response missing messageId, but continuing:', aiResponse);
          // Don't throw error here - the message might still have been sent successfully
        }

        console.log('🎯 useAIAgentMode: ✅ AI response successfully processed and saved:', {
          messageId: aiResponse.messageId,
          responseLength: aiResponse.suggestion?.length,
          timestamp: new Date().toISOString()
        });

        // AI Auto response handled - processing completed by workflow-executor
        console.log('✅ AI Auto: Response processed successfully', {
          messageId: aiResponse?.message_id,
          content: aiResponse?.response?.substring(0, 50) + '...',
          conversationId
        });
      } finally {
        // Always clear the pending request marker
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom window property
        delete (window as any).pendingAIRequests[requestKey];
      }

        console.log('🎯 useAIAgentMode: Successfully triggered AI for unanswered message');

    } catch (error) {
      console.error('🎯 useAIAgentMode: Error in processUnansweredMessages:', error);
      
      // Only show error toast for non-timeout/non-abort errors
      if (!error.message?.includes('aborted') && !error.message?.includes('503')) {
        toast({
          title: t('aiProcessingErrorTitle'),
          description: t('aiProcessingErrorDescription'),
          variant: "destructive",
        });
      }
    }
  };

  // Define the function for processing unanswered messages specifically for suggestions mode
  const processUnansweredMessagesForSuggestions = async (
    tenantId: string, 
    tenantSchema: string, 
    conversationId: string,
    generateAISuggestion?: (messageContent: string) => Promise<void>
  ) => {
    try {
      console.log('🚀 AI SUGGESTION FLOW: Checking for unresponded customer messages when switching to suggestions mode...');
      
      // Get recent messages from this conversation
      const { data: messages, error: messagesError } = await supabase.rpc('get_tenant_messages_direct', {
        tenant_schema: tenantSchema,
        conversation_id_param: conversationId,
        limit_count: 10 // Get last 10 messages to check for unresponded ones
      });

      if (messagesError) {
        console.error('🚀 AI SUGGESTION FLOW: Error fetching messages for unresponded check:', messagesError);
        return;
      }

      if (!messages || messages.length === 0) {
        console.log('🚀 AI SUGGESTION FLOW: No messages found in conversation');
        return;
      }

      // Sort messages by creation time (newest first)
      const sortedMessages = messages.sort((a: Record<string, unknown>, b: Record<string, unknown>) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      // Find the last customer message
      const lastCustomerMessage = sortedMessages.find((msg: Record<string, unknown>) => msg.sender_type === 'customer');
      
      if (!lastCustomerMessage) {
        console.log('🚀 AI SUGGESTION FLOW: No customer messages found');
        return;
      }

      // Check if there's any AI or agent response after the last customer message
      const customerMessageTime = new Date(lastCustomerMessage.created_at).getTime();
      const hasSubsequentResponse = sortedMessages.some((msg: Record<string, unknown>) => {
        const msgTime = new Date(msg.created_at).getTime();
        const isResponse = msgTime > customerMessageTime && 
               (msg.sender_type === 'user' || msg.sender_type === 'agent' || msg.sender_type.startsWith('ai'));
        
        if (isResponse) {
          console.log(`🚀 AI SUGGESTION FLOW: Found response after customer message - sender_type: ${msg.sender_type}`);
        }
        
        return isResponse;
      });

      if (hasSubsequentResponse) {
        console.log('🚀 AI SUGGESTION FLOW: Customer message already has a response, not triggering suggestions');
        return;
      }

      console.log('🚀 AI SUGGESTION FLOW: Found unresponded customer message, triggering AI suggestion generation...', {
        messageId: lastCustomerMessage.id,
        content: lastCustomerMessage.content?.substring(0, 50),
        timestamp: new Date().toISOString()
      });
      
      // Show user feedback
      toast({
        title: t('aiAnalyzingPreviousTitle'),
        description: t('aiAnalyzingPreviousDescription'),
        duration: 4000,
      });

      // Directly call generateAISuggestion if provided
      if (generateAISuggestion && lastCustomerMessage.content) {
        console.log('🚀 AI SUGGESTION FLOW: Calling generateAISuggestion directly with effectiveTenantId:', effectiveTenantId);
        try {
          await generateAISuggestion(lastCustomerMessage.content);
          console.log('🚀 AI SUGGESTION FLOW: generateAISuggestion completed successfully');
        } catch (suggestionError) {
          console.error('🚀 AI SUGGESTION FLOW: Error in generateAISuggestion:', suggestionError);
          // Don't re-throw - let the parent handle it or continue processing
        }
      } else {
        console.log('🚀 AI SUGGESTION FLOW: No generateAISuggestion function provided or no message content');
      }

    } catch (error) {
      console.error('🚀 AI SUGGESTION FLOW: Error in processUnansweredMessagesForSuggestions:', error);

      toast({
        title: t('aiSuggestionErrorTitle'),
        description: t('aiSuggestionErrorDescription'),
        variant: "destructive",
      });
    }
  };

  // Load initial state
  useEffect(() => {
    loadMode();
  }, [loadMode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (modeChangeTimeoutRef.current) {
        clearTimeout(modeChangeTimeoutRef.current);
      }
    };
  }, []);

  return {
    aiMode,
    loading,
    isProcessing,
    changeMode,
    processUnansweredMessages: useCallback(async () => {
      if (!effectiveTenantId) return;
      const tenantSchema = await getTenantSchema();
      if (tenantSchema) {
        await processUnansweredMessages(effectiveTenantId, tenantSchema, conversationId, aiMode);
      }
    }, [effectiveTenantId, conversationId, aiMode, getTenantSchema])
  };
}